import { useAppTheme } from "@hooks/useAppTheme";
import { useDevice } from "@hooks/useDevice";
import { Box, MarkdownRenderer } from "@memorang/ui";
import { Image } from "expo-image";
import { useEffect, useState } from "react";
import { Pressable } from "react-native";
import { Caption, Text } from "react-native-paper";
import {
	type ProethicaPrinciple,
	getPrincipleAsset,
	parsePrincipleDescription,
} from "./constants";

type Props = {
	principles: ProethicaPrinciple[];
	onAllViewed?: () => void;
};

const EmphasizedPrincipleTitle = ({
	title,
	color,
	fontSize = 18,
}: {
	title: string;
	color: string;
	fontSize?: number;
}) => {
	const theme = useAppTheme();
	const firstLetter = title.slice(0, 1);
	const rest = title.slice(1);

	return (
		<Text
			style={{
				color: theme.colors.onSurface,
				fontWeight: "700",
				fontSize,
				lineHeight: fontSize * 1.25,
			}}
		>
			<Text
				style={{
					color,
					fontSize: fontSize + 4,
					fontWeight: "900",
				}}
			>
				{firstLetter}
			</Text>
			{rest}
		</Text>
	);
};

const LetterTabs = ({
	principles,
	selectedIndex,
	visited,
	onSelect,
}: {
	principles: ProethicaPrinciple[];
	selectedIndex: number;
	visited: Set<number>;
	onSelect: (index: number) => void;
}) => {
	const theme = useAppTheme();

	return (
		<Box flexDirection="row" gap={6}>
			{principles.map((principle, index) => {
				const asset = getPrincipleAsset(principle.title, index);
				const assetColor = theme.dark
					? (asset.darkColor ?? asset.color)
					: asset.color;
				const selected = index === selectedIndex;
				const isVisited = visited.has(index);
				const letter = principle.title.slice(0, 1).toUpperCase();

				return (
					<Pressable
						key={principle.title}
						onPress={() => onSelect(index)}
						accessibilityRole="tab"
						accessibilityState={{ selected }}
						accessibilityLabel={principle.title}
						style={{ flex: 1, minWidth: 0 }}
					>
						<Box
							position="relative"
							alignItems="center"
							justifyContent="center"
							paddingVertical={10}
							borderRadius={10}
							borderWidth={2}
							borderColor={selected ? assetColor : theme.colors.outlineVariant}
							backgroundColor={
								selected ? `${assetColor}1A` : theme.colors.surface
							}
						>
							{isVisited ? null : (
								<Box
									position="absolute"
									top={6}
									right={6}
									width={8}
									height={8}
									borderRadius={4}
									backgroundColor={theme.colors.error}
								/>
							)}
							<Text
								style={{
									color: assetColor,
									fontSize: 20,
									fontWeight: "900",
								}}
							>
								{letter}
							</Text>
						</Box>
					</Pressable>
				);
			})}
		</Box>
	);
};

const PrincipleDetail = ({
	principle,
	index,
}: {
	principle: ProethicaPrinciple;
	index: number;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const asset = getPrincipleAsset(principle.title, index);
	const assetColor = theme.dark
		? (asset.darkColor ?? asset.color)
		: asset.color;
	const parsed = parsePrincipleDescription(principle);

	return (
		<Box gap={20}>
			<Box
				flexDirection={isMobile ? "column" : "row"}
				gap={16}
				alignItems={isMobile ? "stretch" : "flex-start"}
			>
				<Box
					width={isMobile ? "100%" : 140}
					height={140}
					borderRadius={12}
					backgroundColor="#FAF4ED"
					borderWidth={1}
					borderColor={`${assetColor}33`}
					overflow="hidden"
					alignItems="center"
					justifyContent="center"
				>
					<Image
						source={asset.image}
						contentFit="contain"
						style={{ width: "100%", height: "100%" }}
						alt=""
					/>
				</Box>
				<Box flex={1} gap={10}>
					<EmphasizedPrincipleTitle
						title={principle.title}
						color={assetColor}
						fontSize={20}
					/>
					<MarkdownRenderer
						text={parsed.body}
						fontSize={14}
						paragraphMarginVertical={0}
						customStyles={{
							text: { color: theme.colors.onSurface, lineHeight: 22 },
						}}
					/>
				</Box>
			</Box>

			{parsed.guidingQuestions.length ? (
				<Box gap={12}>
					<Caption
						style={{
							color: theme.colors.primary,
							fontWeight: "800",
							letterSpacing: 0.9,
						}}
					>
						GUIDING QUESTIONS
					</Caption>
					{parsed.guidingQuestions.map((question, qIndex) => (
						<Box
							key={question}
							flexDirection="row"
							gap={12}
							alignItems="flex-start"
						>
							<Box
								width={22}
								height={22}
								borderRadius={11}
								backgroundColor={theme.colors.primaryContainer}
								alignItems="center"
								justifyContent="center"
								marginTop={1}
							>
								<Text
									style={{
										color: theme.colors.primary,
										fontSize: 12,
										fontWeight: "800",
									}}
								>
									{qIndex + 1}
								</Text>
							</Box>
							<Box flex={1}>
								<MarkdownRenderer
									text={question}
									fontSize={14}
									paragraphMarginVertical={0}
									customStyles={{
										text: { color: theme.colors.primary, lineHeight: 22 },
									}}
								/>
							</Box>
						</Box>
					))}
				</Box>
			) : null}
		</Box>
	);
};

export const ProethicaPrinciplesTabs = ({ principles, onAllViewed }: Props) => {
	const theme = useAppTheme();
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [visited, setVisited] = useState(() => new Set<number>([0]));

	const allVisited = principles.length > 0 && visited.size >= principles.length;

	useEffect(() => {
		if (allVisited) {
			onAllViewed?.();
		}
	}, [allVisited, onAllViewed]);

	if (!principles.length) {
		return null;
	}

	const safeIndex = Math.min(selectedIndex, principles.length - 1);
	const positionLabel = `${String(safeIndex + 1).padStart(2, "0")} / ${String(
		principles.length,
	).padStart(2, "0")}`;

	return (
		<Box
			borderWidth={1}
			borderColor={theme.colors.outlineVariant}
			borderRadius={16}
			overflow="hidden"
			backgroundColor={theme.colors.surface}
		>
			<Box
				paddingHorizontal={18}
				paddingVertical={14}
				backgroundColor={theme.colors.surfaceVariant}
				borderBottomWidth={1}
				borderBottomColor={theme.colors.outlineVariant}
				gap={12}
			>
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="space-between"
				>
					<Caption
						style={{
							letterSpacing: 1,
							color: theme.colors.onSurfaceVariant,
							fontWeight: "800",
						}}
					>
						PROETHICS PRINCIPLES
					</Caption>
					<Text
						variant="labelMedium"
						style={{
							color: theme.colors.onSurfaceVariant,
							fontWeight: "700",
						}}
					>
						{positionLabel}
					</Text>
				</Box>
				<LetterTabs
					principles={principles}
					selectedIndex={safeIndex}
					visited={visited}
					onSelect={(index) => {
						setSelectedIndex(index);
						setVisited((current) => new Set(current).add(index));
					}}
				/>
			</Box>
			<Box padding={18}>
				<PrincipleDetail principle={principles[safeIndex]} index={safeIndex} />
			</Box>
		</Box>
	);
};
